import React, { useEffect, useRef, useState } from "react";
import {
  Landmark,
  Plus,
  Trash2,
  Save,
  Loader2,
  Upload,
  History,
  ToggleLeft,
  ToggleRight,
  Sparkles,
  QrCode,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, getFileUrl } from "@/lib/api";

/**
 * Admin panel: multi-account Bank Details manager. Renders:
 *   - Settings section (important notice, google sheet, master toggles)
 *   - Accounts list with per-row Edit / Toggle-Active / Delete
 *   - "Add new account" inline form
 *   - Audit log (last 20 changes)
 *
 * The whole thing hits the singleton `/api/bank-details` — every write
 * refetches the doc so the UI never drifts.
 */
export default function BankAccountManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null); // {is_new?, ...fields}
  const [notice, setNotice] = useState("");
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetEnabled, setSheetEnabled] = useState(true);
  const [tabEnabled, setTabEnabled] = useState(true);

  const load = async () => {
    try {
      const { data: fresh } = await api.get("/bank-details");
      setData(fresh);
      setNotice(fresh.important_notice || "");
      setSheetUrl(fresh.google_sheet_url || "");
      setSheetEnabled(!!fresh.google_sheet_enabled);
      setTabEnabled(!!fresh.tab_enabled);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data: fresh } = await api.put("/bank-details/settings", {
        important_notice: notice,
        google_sheet_url: sheetUrl,
        google_sheet_enabled: sheetEnabled,
        tab_enabled: tabEnabled,
      });
      setData(fresh);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleActive = async (acc) => {
    try {
      const { data: fresh } = await api.post(
        `/bank-details/accounts/${acc.account_id}/toggle-active`,
        { is_active: !acc.is_active }
      );
      setData(fresh);
      toast.success(acc.is_active ? "Account deactivated" : "Account activated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteAccount = async (acc) => {
    if (!window.confirm(`Delete "${acc.label}"? This cannot be undone.`)) return;
    try {
      const { data: fresh } = await api.delete(
        `/bank-details/accounts/${acc.account_id}`
      );
      setData(fresh);
      toast.success("Account deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-[#385C4B]" />
      </div>
    );
  }

  const accounts = data?.accounts || [];

  return (
    <div className="space-y-4" data-testid="bank-account-manager">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Landmark className="w-5 h-5 text-[#385C4B]" strokeWidth={2} />
        <h2
          className="text-lg font-semibold text-[#1C1C19]"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Bank Account Manager
        </h2>
      </div>

      {/* Settings section */}
      <div className="bg-white border border-[#DCDCD3] rounded-xl p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-[#6B6B61] font-semibold">
          Settings
        </div>

        <div>
          <label className="text-xs text-[#1C1C19] font-medium">
            Important Notice (shown at top of Bank Details page)
          </label>
          <textarea
            value={notice}
            onChange={(e) => setNotice(e.target.value.slice(0, 1000))}
            rows={3}
            placeholder="e.g. Please verify account details every day before depositing"
            data-testid="bank-notice-input"
            className="w-full mt-1 px-3 py-2 text-xs bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg focus:outline-none focus:border-[#385C4B]"
          />
          <div className="text-[10px] text-[#6B6B61] mt-0.5 text-right">
            {notice.length} / 1000
          </div>
        </div>

        <div>
          <label className="text-xs text-[#1C1C19] font-medium">Google Sheet URL</label>
          <input
            type="url"
            value={sheetUrl}
            onChange={(e) => setSheetUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/..."
            data-testid="bank-sheet-url-input"
            className="w-full mt-1 px-3 py-2 text-xs bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg font-mono focus:outline-none focus:border-[#385C4B]"
          />
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <ToggleRow
            label="Google Sheet link visible to members"
            value={sheetEnabled}
            onChange={setSheetEnabled}
            testid="bank-sheet-toggle"
          />
          <ToggleRow
            label="Bank Details tab visible to members"
            value={tabEnabled}
            onChange={setTabEnabled}
            testid="bank-tab-toggle"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={saveSettings}
            disabled={savingSettings}
            data-testid="bank-save-settings-btn"
            className="bg-[#385C4B] text-white text-xs px-4 py-2 rounded-lg hover:bg-[#2D4A3C] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {savingSettings ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            Save settings
          </button>
        </div>
      </div>

      {/* Accounts list */}
      <div className="bg-white border border-[#DCDCD3] rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-[#6B6B61] font-semibold">
            Accounts ({accounts.length})
          </div>
          <button
            type="button"
            onClick={() =>
              setEditingAccount({
                is_new: true,
                label: "",
                account_holder_name: "",
                account_number: "",
                ifsc: "",
                bank_name: "",
                branch: "",
                upi_id: "",
                qr_code_file_id: null,
                is_active: true,
              })
            }
            data-testid="bank-add-account-btn"
            className="bg-[#385C4B] text-white text-xs px-3 py-1.5 rounded-lg hover:bg-[#2D4A3C] transition-colors flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            Add account
          </button>
        </div>

        {accounts.length === 0 ? (
          <div className="text-xs text-[#6B6B61] italic py-4 text-center">
            No accounts yet — click "Add account" to create one.
          </div>
        ) : (
          <div className="space-y-2">
            {accounts.map((acc) => (
              <div
                key={acc.account_id}
                data-testid={`bank-admin-row-${acc.account_id}`}
                className="border border-[#DCDCD3] rounded-lg p-3 flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-md bg-[#385C4B]/10 flex items-center justify-center flex-shrink-0">
                  <Landmark className="w-4 h-4 text-[#385C4B]" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1C1C19] truncate">
                    {acc.label}
                  </div>
                  <div className="text-[11px] text-[#6B6B61] font-mono truncate">
                    {acc.bank_name} · ****{acc.account_number.slice(-4)} · {acc.ifsc}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleActive(acc)}
                  data-testid={`bank-toggle-${acc.account_id}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full flex items-center gap-1 ${
                    acc.is_active
                      ? "bg-[#385C4B]/10 text-[#385C4B]"
                      : "bg-[#F5F5F0] text-[#6B6B61]"
                  }`}
                >
                  {acc.is_active ? (
                    <ToggleRight className="w-3.5 h-3.5" strokeWidth={2} />
                  ) : (
                    <ToggleLeft className="w-3.5 h-3.5" strokeWidth={2} />
                  )}
                  {acc.is_active ? "Active" : "Inactive"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingAccount({ ...acc, is_new: false })}
                  data-testid={`bank-edit-${acc.account_id}`}
                  className="text-[11px] px-2.5 py-1 rounded-lg border border-[#DCDCD3] hover:border-[#385C4B]/50 text-[#385C4B]"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => deleteAccount(acc)}
                  data-testid={`bank-delete-${acc.account_id}`}
                  className="p-1.5 rounded-lg text-[#C8553D] hover:bg-[#C8553D]/10"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR Codes section — independent of bank accounts */}
      <QRCodesSection
        qrs={data?.qr_codes || []}
        onChanged={(fresh) => setData(fresh)}
      />

      {/* Audit log */}
      {data?.audit_log?.length > 0 && (
        <div className="bg-white border border-[#DCDCD3] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <History className="w-3.5 h-3.5 text-[#6B6B61]" strokeWidth={2} />
            <div className="text-[11px] uppercase tracking-widest text-[#6B6B61] font-semibold">
              Recent changes
            </div>
          </div>
          <div className="space-y-1">
            {data.audit_log.slice(0, 10).map((row, i) => (
              <div
                key={i}
                className="text-[11px] text-[#6B6B61] font-mono flex items-center gap-2"
              >
                <span className="text-[#385C4B] font-semibold">{row.by_name}</span>
                <span>·</span>
                <span>{row.action}</span>
                {row.details && (
                  <>
                    <span>·</span>
                    <span className="text-[#1C1C19] truncate">{row.details}</span>
                  </>
                )}
                <span className="ml-auto text-[10px]">
                  {new Date(row.when).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {editingAccount && (
        <AccountEditorModal
          initial={editingAccount}
          onClose={() => setEditingAccount(null)}
          onSaved={(fresh) => {
            setData(fresh);
            setEditingAccount(null);
          }}
        />
      )}
    </div>
  );
}

function ToggleRow({ label, value, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      data-testid={testid}
      className={`flex items-center gap-2 text-xs px-2 py-1 rounded-lg transition-colors ${
        value ? "bg-[#385C4B]/10 text-[#385C4B]" : "bg-[#F5F5F0] text-[#6B6B61]"
      }`}
    >
      {value ? (
        <ToggleRight className="w-4 h-4" strokeWidth={2} />
      ) : (
        <ToggleLeft className="w-4 h-4" strokeWidth={2} />
      )}
      {label}
    </button>
  );
}

function AccountEditorModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.label || !form.account_holder_name || !form.account_number || !form.ifsc || !form.bank_name) {
      toast.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        account_holder_name: form.account_holder_name.trim(),
        account_number: form.account_number.trim(),
        ifsc: form.ifsc.trim().toUpperCase(),
        bank_name: form.bank_name.trim(),
        branch: (form.branch || "").trim(),
        upi_id: (form.upi_id || "").trim(),
        is_active: !!form.is_active,
      };
      let fresh;
      if (form.is_new) {
        ({ data: fresh } = await api.post("/bank-details/accounts", payload));
      } else {
        ({ data: fresh } = await api.put(
          `/bank-details/accounts/${form.account_id}`,
          payload
        ));
      }
      toast.success(form.is_new ? "Account added" : "Account updated");
      onSaved(fresh);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bank-account-editor"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#DCDCD3] bg-[#F5F5F0]">
          <div
            className="text-sm font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {form.is_new ? "Add Account" : "Edit Account"}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-[#6B6B61] hover:bg-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="text-[10px] text-[#6B6B61] bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg px-3 py-2 flex items-start gap-2">
            <QrCode className="w-3.5 h-3.5 text-[#385C4B] flex-shrink-0 mt-0.5" />
            <span>
              QR codes are managed separately below — add PhonePe / Paytm / GPay QRs from the
              "QR Codes" section after saving this account.
            </span>
          </div>
          <Field label="Label" required value={form.label} onChange={upd("label")} placeholder="e.g. SBI - Preet Vihar" testid="bank-edit-label" />
          <Field label="Account Holder Name" required value={form.account_holder_name} onChange={upd("account_holder_name")} testid="bank-edit-holder" />
          <Field label="Account Number" required value={form.account_number} onChange={upd("account_number")} mono testid="bank-edit-account-number" />
          <Field label="IFSC Code" required value={form.ifsc} onChange={upd("ifsc")} mono testid="bank-edit-ifsc" />
          <Field label="Bank Name" required value={form.bank_name} onChange={upd("bank_name")} testid="bank-edit-bank" />
          <Field label="Branch" value={form.branch} onChange={upd("branch")} testid="bank-edit-branch" />
          <Field label="UPI ID" value={form.upi_id} onChange={upd("upi_id")} mono placeholder="preet@paytm" testid="bank-edit-upi" />

          <ToggleRow
            label="Active (visible to members)"
            value={form.is_active}
            onChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
            testid="bank-edit-active-toggle"
          />
        </div>

        <div className="px-5 py-3 border-t border-[#DCDCD3] bg-[#F5F5F0] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-[#6B6B61] px-3 py-2 rounded-lg hover:bg-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="bank-edit-save-btn"
            className="bg-[#385C4B] text-white text-xs px-4 py-2 rounded-lg hover:bg-[#2D4A3C] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            {form.is_new ? "Add account" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, value, onChange, placeholder, mono, testid }) {
  return (
    <div>
      <label className="text-xs text-[#1C1C19] font-medium">
        {label}
        {required && <span className="text-[#C8553D] ml-1">*</span>}
      </label>
      <input
        type="text"
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full mt-1 px-3 py-2 text-xs bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg focus:outline-none focus:border-[#385C4B] ${
          mono ? "font-mono" : ""
        }`}
      />
    </div>
  );
}


// Dropdown options for QR labels — synced with backend _QR_LABEL_TYPES.
const QR_LABEL_OPTIONS = [
  { value: "phonepe", display: "PhonePe" },
  { value: "paytm", display: "Paytm" },
  { value: "gpay", display: "Google Pay" },
  { value: "bhim", display: "BHIM" },
  { value: "bank", display: "Bank Direct UPI" },
  { value: "whatsapp", display: "WhatsApp Pay" },
  { value: "other", display: "Other (custom label)" },
];

function qrDisplayLabel(qr) {
  const custom = (qr.label_custom || "").trim();
  if (qr.label_type === "other") return custom || "Custom QR";
  const opt = QR_LABEL_OPTIONS.find((o) => o.value === qr.label_type);
  const base = opt ? opt.display : qr.label_type;
  return custom ? `${base} — ${custom}` : base;
}

/** Admin-only QR codes list: add / edit / toggle / delete. Independent
 *  of the bank accounts list — members can scan any enabled QR to pay,
 *  regardless of which account it belongs to. */
function QRCodesSection({ qrs, onChanged }) {
  const [editing, setEditing] = useState(null);

  const toggle = async (qr) => {
    try {
      const { data: fresh } = await api.post(
        `/bank-details/qr-codes/${qr.qr_id}/toggle`,
        { is_enabled: !qr.is_enabled }
      );
      onChanged(fresh);
      toast.success(qr.is_enabled ? "QR disabled" : "QR enabled");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (qr) => {
    if (!window.confirm(`Delete "${qrDisplayLabel(qr)}"? The image will be removed permanently.`)) return;
    try {
      const { data: fresh } = await api.delete(`/bank-details/qr-codes/${qr.qr_id}`);
      onChanged(fresh);
      toast.success("QR deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-white border border-[#DCDCD3] rounded-xl p-4 space-y-3" data-testid="bank-qr-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <QrCode className="w-4 h-4 text-[#385C4B]" strokeWidth={2} />
          <div className="text-[11px] uppercase tracking-widest text-[#6B6B61] font-semibold">
            QR Codes ({qrs.length})
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            setEditing({
              is_new: true,
              label_type: "phonepe",
              label_custom: "",
              file_id: "",
              is_enabled: true,
            })
          }
          data-testid="bank-add-qr-btn"
          className="bg-[#385C4B] text-white text-xs px-3 py-1.5 rounded-lg hover:bg-[#2D4A3C] transition-colors flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Add QR
        </button>
      </div>
      <div className="text-[11px] text-[#6B6B61] leading-relaxed">
        QR codes work independently — a QR is visible to members whenever it's enabled, no matter which bank account it's associated with.
      </div>

      {qrs.length === 0 ? (
        <div className="text-xs text-[#6B6B61] italic py-4 text-center">
          No QR codes yet — click "Add QR" to upload PhonePe / Paytm / GPay codes.
        </div>
      ) : (
        <div className="space-y-2">
          {qrs.map((qr) => (
            <div
              key={qr.qr_id}
              data-testid={`bank-qr-row-${qr.qr_id}`}
              className="border border-[#DCDCD3] rounded-lg p-3 flex items-center gap-3"
            >
              {qr.file_id ? (
                <img
                  src={getFileUrl(qr.file_id)}
                  alt=""
                  className="w-12 h-12 object-contain bg-[#F5F5F0] border border-[#DCDCD3] rounded-md flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 bg-[#F5F5F0] border border-dashed border-[#DCDCD3] rounded-md flex items-center justify-center flex-shrink-0">
                  <QrCode className="w-5 h-5 text-[#DCDCD3]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#1C1C19] truncate">
                  {qrDisplayLabel(qr)}
                </div>
                <div className="text-[10px] text-[#6B6B61] font-mono">
                  {qr.label_type.toUpperCase()}
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggle(qr)}
                data-testid={`bank-qr-toggle-${qr.qr_id}`}
                className={`text-[11px] px-2.5 py-1 rounded-full flex items-center gap-1 ${
                  qr.is_enabled
                    ? "bg-[#385C4B]/10 text-[#385C4B]"
                    : "bg-[#F5F5F0] text-[#6B6B61]"
                }`}
              >
                {qr.is_enabled ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                {qr.is_enabled ? "Enabled" : "Disabled"}
              </button>
              <button
                type="button"
                onClick={() => setEditing({ ...qr, is_new: false })}
                data-testid={`bank-qr-edit-${qr.qr_id}`}
                className="text-[11px] px-2.5 py-1 rounded-lg border border-[#DCDCD3] hover:border-[#385C4B]/50 text-[#385C4B]"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => del(qr)}
                data-testid={`bank-qr-delete-${qr.qr_id}`}
                className="p-1.5 rounded-lg text-[#C8553D] hover:bg-[#C8553D]/10"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <QREditorModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={(fresh) => {
            onChanged(fresh);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function QREditorModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadQr = async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("QR must be an image (JPG/PNG)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("QR image too large. Max 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, file_id: data.file_id }));
      toast.success("QR uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.file_id) {
      toast.error("Please upload a QR image first");
      return;
    }
    if (form.label_type === "other" && !(form.label_custom || "").trim()) {
      toast.error("Custom label is required when Type is 'Other'");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label_type: form.label_type,
        label_custom: (form.label_custom || "").trim(),
        file_id: form.file_id,
        is_enabled: !!form.is_enabled,
      };
      let fresh;
      if (form.is_new) {
        ({ data: fresh } = await api.post("/bank-details/qr-codes", payload));
      } else {
        ({ data: fresh } = await api.put(`/bank-details/qr-codes/${form.qr_id}`, payload));
      }
      toast.success(form.is_new ? "QR added" : "QR updated");
      onSaved(fresh);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bank-qr-editor"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#DCDCD3] bg-[#F5F5F0]">
          <div
            className="text-sm font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {form.is_new ? "Add QR Code" : "Edit QR Code"}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-[#6B6B61] hover:bg-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-[#1C1C19] font-medium">
              Type <span className="text-[#C8553D]">*</span>
            </label>
            <select
              value={form.label_type}
              onChange={(e) => setForm((f) => ({ ...f, label_type: e.target.value }))}
              data-testid="bank-qr-type"
              className="w-full mt-1 px-3 py-2 text-xs bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg focus:outline-none focus:border-[#385C4B]"
            >
              {QR_LABEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.display}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-[#1C1C19] font-medium">
              Custom label
              {form.label_type === "other" && (
                <span className="text-[#C8553D] ml-1">*</span>
              )}
              <span className="text-[10px] text-[#6B6B61] ml-1 font-normal">
                (optional unless Type = Other)
              </span>
            </label>
            <input
              type="text"
              value={form.label_custom || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, label_custom: e.target.value.slice(0, 40) }))
              }
              placeholder="e.g. Preet Business QR"
              data-testid="bank-qr-custom"
              className="w-full mt-1 px-3 py-2 text-xs bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg focus:outline-none focus:border-[#385C4B]"
            />
          </div>

          <div>
            <label className="text-xs text-[#1C1C19] font-medium">
              QR Image (JPG/PNG, max 5 MB) <span className="text-[#C8553D]">*</span>
            </label>
            <div className="flex items-start gap-3 mt-1">
              {form.file_id ? (
                <img
                  src={getFileUrl(form.file_id)}
                  alt="QR preview"
                  className="w-24 h-24 object-contain bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg"
                />
              ) : (
                <div className="w-24 h-24 bg-[#F5F5F0] border border-dashed border-[#DCDCD3] rounded-lg flex items-center justify-center text-[10px] text-[#6B6B61]">
                  no image
                </div>
              )}
              <div className="flex-1">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={uploadQr}
                  className="hidden"
                  data-testid="bank-qr-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="bank-qr-upload-btn"
                  className="text-xs bg-white border border-[#DCDCD3] hover:border-[#385C4B]/50 px-3 py-2 rounded-lg flex items-center gap-2 text-[#385C4B]"
                >
                  {uploading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {form.file_id ? "Replace image" : "Upload QR"}
                </button>
              </div>
            </div>
          </div>

          <ToggleRow
            label="Enabled (visible to members)"
            value={form.is_enabled}
            onChange={(v) => setForm((f) => ({ ...f, is_enabled: v }))}
            testid="bank-qr-enabled-toggle"
          />
        </div>

        <div className="px-5 py-3 border-t border-[#DCDCD3] bg-[#F5F5F0] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-[#6B6B61] px-3 py-2 rounded-lg hover:bg-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="bank-qr-save-btn"
            className="bg-[#385C4B] text-white text-xs px-4 py-2 rounded-lg hover:bg-[#2D4A3C] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            {form.is_new ? "Add QR" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
